import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { CompanyStore } from "services/company/companyStore";
import type { ConsentStore } from "services/consent/consentStore";
import { ConsentType } from "services/consent/consentTypes";
import { AcspFormOutcome, getAcspFormOutcome } from "services/director/acspFormOutcome";
import type { DirectorStore } from "services/director/directorStore";
import type { FeatureStore } from "services/feature/featureStore";
import type { IncorporationStore } from "services/incorporation/incorporationStore";
import { IncorporationStatus } from "services/incorporation/incorporationTypes";
import { IssueKey, IssuesStore } from "services/issues/issuesStore";
import { QuestionsStore } from "services/questions/questionsStore";
import {
  CHVerificationQuestionV2,
  ISSUE_FIELD_TO_QUESTION,
  ISSUE_REJECTION_SUBMISSION_QUESTION,
} from "./chVerificationConstantsV2";

export class CHVerificationQuestionStoreV2 extends QuestionsStore<CHVerificationQuestionV2> {
  protected _issueKey = IssueKey.CompanyHouse;
  protected _issueFieldsToQuestions = ISSUE_FIELD_TO_QUESTION;
  protected _issueFieldRejectionSubmissionQuestions = ISSUE_REJECTION_SUBMISSION_QUESTION; // Force to move to next step

  constructor(
    protected readonly _directorStore: DirectorStore,
    protected readonly _companyStore: CompanyStore,
    protected readonly _featureStore: FeatureStore,
    protected readonly _incorporationStore: IncorporationStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _issuesStore: IssuesStore,
    protected readonly _consentStore: ConsentStore,
  ) {
    super(CHVerificationQuestionV2, _issuesStore);
  }

  get isAcspIdvConsentGranted(): boolean {
    return this._consentStore.consentsData.includes(ConsentType.AcspIdentityVerification);
  }

  protected _shouldSkipQuestion(question: CHVerificationQuestionV2): boolean {
    const { directors, mainDirector } = this._directorStore;
    const status = mainDirector?.identityVerificationStatus;
    const isCodeExists = mainDirector?.chPersonalCode !== undefined && mainDirector?.chPersonalCode !== "";

    // Skip if the status is undefined
    if (question === CHVerificationQuestionV2.HasPersonalCode) {
      return status !== undefined || isCodeExists;
    }

    // Skip IdvConsent if user said they have a code, if ANNA isn't acting as ACSP verifier
    // (self-service flow — recording ACSP consent for a service that never runs is misleading),
    // or if consent has already been granted (avoid re-asking and re-POSTing on resume/back-nav).
    if (question === CHVerificationQuestionV2.IdvConsent) {
      return (
        isCodeExists ||
        status !== undefined ||
        !this._featureStore.hasFeature("formationAcspIdvSubmissionEnabled") ||
        this.isAcspIdvConsentGranted
      );
    }

    // Skip RequestIdConfirmation if user said they have a code
    if (question === CHVerificationQuestionV2.RequestIdConfirmation) {
      return isCodeExists || status !== undefined;
    }

    if (question === CHVerificationQuestionV2.IdConfirmationProcessing) {
      return isCodeExists || !this._featureStore.hasFeature("formationAcspIdvSubmissionEnabled");
    }

    // MultiDirectorVerification: Show only if main completed AND not started for secondaries AND multiple directors need codes
    if (question === CHVerificationQuestionV2.MultiDirectorVerification) {
      return directors.length <= 1;
    }

    return super._shouldSkipQuestion(question);
  }

  protected _getPreviousQuestionKey(): CHVerificationQuestionV2 | undefined {
    const prevQuestionKey = super._getPreviousQuestionKey();
    const { mainDirector } = this._directorStore;

    // Back-navigation is intentionally asymmetric. One step back is fine while nothing
    // irreversible has happened yet: RequestIdConfirmation -> IdvConsent, IdvConsent ->
    // HasPersonalCode (both fall through below, unaffected by this switch). But once an ACSP
    // verification request has been submitted (i.e. once IdConfirmationProcessing is reached),
    // going back jumps all the way to HasPersonalCode, skipping RequestIdConfirmation and
    // IdvConsent too — you can't casually back into a request that's already gone to Companies House.
    switch (prevQuestionKey) {
      case CHVerificationQuestionV2.RequestIdConfirmation:
      case CHVerificationQuestionV2.IdConfirmationProcessing:
        return mainDirector?.identityVerificationStatus === undefined
          ? CHVerificationQuestionV2.HasPersonalCode
          : undefined;
    }
    return prevQuestionKey;
  }

  protected _getRelevantQuestion(): CHVerificationQuestionV2 | undefined {
    const { mainDirector } = this._directorStore;
    const { incorporation } = this._incorporationStore;

    const status = mainDirector?.identityVerificationStatus;
    const hasCode = mainDirector?.chPersonalCode !== undefined && mainDirector?.chPersonalCode !== "";

    this._analyticsManager.event("ch_verification_v2_status_check", {
      IdVerificationStatus: status ?? "undefined",
      hasCode,
    });

    if (incorporation?.status !== IncorporationStatus.Rejected) {
      // The form hasn't been sent yet - wait for it on the processing screen, as right after the request
      if (
        !hasCode &&
        getAcspFormOutcome(mainDirector) === AcspFormOutcome.Pending &&
        this._featureStore.hasFeature("formationAcspIdvSubmissionEnabled")
      ) {
        return CHVerificationQuestionV2.IdConfirmationProcessing;
      }
      if (hasCode || status !== undefined) {
        return CHVerificationQuestionV2.EnterPersonalCode;
      }
    }

    // Start from beginning - ask if they have code
    return undefined;
  }
}
